import { useState, useEffect, useRef } from "react";
import { Card, Image, Button, Modal, Form } from "react-bootstrap";
import { useGetCompanyPropertyDetailsQuery, useChangeCompanyDetailsMutation } from "../../lib/apiSlice/property/propertySlice.tsx";
import { serverUrl } from "../../../../serverUrl.js";
import { toast } from "react-toastify";

export default function CompanyPropertyDetails() {
    const { data, isLoading } = useGetCompanyPropertyDetailsQuery();
    const companyDetails = data?.results?.[0] || {};

    // State for modal visibility
    const [showModal, setShowModal] = useState(false);

    // State for form data
    const [formData, setFormData] = useState({
        name: "",
        phone: "",
        email: "",
        location: "",
        property_type: "",
        cover_image: null,
    });

    // For image preview
    const [imagePreview, setImagePreview] = useState(null);
    const fileInputRef = useRef(null);

    // Update formData with latest companyDetails when modal is opened
    useEffect(() => {
        if (showModal) {
            setFormData({
                name: companyDetails?.name || "",
                phone: companyDetails?.phone || "",
                email: companyDetails?.email || "",
                location: companyDetails?.location || "",
                property_type: companyDetails?.property_type || "",
                cover_image: null,
            });
            setImagePreview(companyDetails?.company_image || null);
        }
    }, [showModal, companyDetails]);

    // Handle modal open/close
    const handleShowModal = () => setShowModal(true);
    const handleCloseModal = () => setShowModal(false);

    // Handle form input changes
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });
    };

    // Handle image change
    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setFormData({ ...formData, cover_image: file });
            setImagePreview(URL.createObjectURL(file));
        }
    };

    // Handle form submission
    const [changeCompanyDetails] = useChangeCompanyDetailsMutation();

    const handleSubmit = (e) => {
        e.preventDefault();
        // Always use FormData for file/image upload
        const payload = new FormData();
        payload.append("name", formData.name);
        payload.append("phone", formData.phone);
        payload.append("email", formData.email);
        payload.append("location", formData.location);
        payload.append("property_type", formData.property_type);
        // Append image if present
        if (formData.cover_image) {
            payload.append("company_image", formData.cover_image);
            payload.append("image", formData.cover_image);
        }

        changeCompanyDetails({
            id: companyDetails.id,
            companyData: payload,
        })
            .unwrap()
            .then((response) => {
                toast.success("Company details updated successfully!");
                handleCloseModal();
            })
            .catch((error) => {
                console.error("Error:", error);
                toast.error("Failed to update company details.");
            });
    };

    if (isLoading) return <div></div>;

    return companyDetails && companyDetails.name ? (
        <div className="tasks ">
            <Card className="border-0 p-0">
                <div className="d-flex justify-content-between">
                    {companyDetails.property_type === "residential" ? (
                        <p
                            className='p-2 w-auto text-uppercase'
                            style={{
                                fontSize: "11px",
                                borderRadius: "5px",
                                width: "fit",
                                color: "white",
                                background: "#8e44ad"
                            }}
                        >
                            {companyDetails.property_type}
                        </p>
                    ) : companyDetails.property_type === "commercial" ? (
                        <p
                            className='p-2 w-auto text-uppercase'
                            style={{
                                fontSize: "11px",
                                borderRadius: "5px",
                                width: "fit",
                                color: "white",
                                background: "#2ecc71"
                            }}
                        >
                            {companyDetails.property_type}
                        </p>
                    ) : (
                        <p
                            className='p-2 w-auto text-uppercase'
                            style={{
                                fontSize: "11px",
                                borderRadius: "5px",
                                width: "fit",
                                color: "white",
                                background: "#1abc9c"
                            }}
                        >
                            {companyDetails.property_type}
                        </p>
                    )}

                    <p
                        className="btn btn-sm"
                        onClick={handleShowModal}
                        style={{ backgroundColor: "#3498db", color: "white", border: "none" }}
                    >
                        <i className="bi bi-pen me-2 p-0 h-auto"></i>
                        Edit
                    </p>
                </div>

                {companyDetails.company_image && (
                    <Image
                        className="rounded-top"
                        src={companyDetails.company_image}
                        alt={companyDetails.name || "Company image"}
                        style={{ height: "200px", objectFit: "cover" }}
                    />
                )}

                <Card.Body className="p-4">
                    <div className="d-flex justify-content-between">
                        <div>
                            <h4>Company Details</h4>
                            <p style={{ color: "#2c3e50" }}>{companyDetails.name}</p>
                            <p style={{ color: "#34495e" }}>{companyDetails.phone}</p>
                            <p style={{ color: "#34495e" }}>{companyDetails.email}</p>
                            <p style={{ color: "#34495e" }}>{companyDetails.location}</p>
                        </div>

                        <div>
                            {companyDetails.kra_compliance && (
                                <>
                                    <h4>KRA Details</h4>
                                    <p style={{ color: "#34495e" }}>{companyDetails.kra_name}</p>
                                    <p style={{ color: "#34495e" }}>{companyDetails.kra_pin_number}</p>
                                </>
                            )}
                        </div>
                    </div>
                </Card.Body>
            </Card>

            {/* Edit Modal */}
            <Modal show={showModal} onHide={handleCloseModal} centered>
                <Modal.Header closeButton style={{ backgroundColor: "#3498db", color: "#ffffff" }}>
                    <Modal.Title>Edit Company Details</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form onSubmit={handleSubmit}>
                        {/* Creative Image Upload Field */}
                        <div className="mb-6 flex flex-col items-center">
                            <p className="block text-lg font-bold mb-2 text-gray-700 text-start">Company Cover Image</p>
                            <div className="relative group w-40 h-40 rounded-full shadow-lg overflow-hidden border-4 border-blue-300 bg-gradient-to-tr from-blue-100 to-purple-100 flex items-center justify-center">
                                {imagePreview ? (
                                    <img
                                        src={imagePreview}
                                        alt="Cover Preview"
                                        className="object-cover w-full h-full transition-transform duration-300 group-hover:scale-105"
                                    />
                                ) : (
                                    <span className="text-gray-400 text-5xl">+</span>
                                )}
                                <div
                                    className="absolute inset-0 bg-black bg-opacity-40 opacity-0 group-hover:opacity-100 flex flex-col items-center justify-center transition-opacity duration-300 cursor-pointer"
                                    onClick={() => fileInputRef.current.click()}
                                >
                                    <span className="text-white text-sm font-semibold mb-2">Change Image</span>
                                    <span className="bg-white text-blue-600 px-3 py-1 rounded-full text-xs shadow">Browse</span>
                                </div>
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    accept="image/*"
                                    className="hidden"
                                    onChange={handleImageChange}
                                />
                            </div>
                            <p className="text-xs text-gray-500 mt-2">Recommended: Square image, max 2MB.</p>
                        </div>
                        <Form.Group className="mb-3">
                            <p>Company Name : </p>
                            <Form.Control
                                type="text"
                                name="name"
                                value={formData.name}
                                onChange={handleInputChange}
                            />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <p>Contact Phone Number : </p>
                            <Form.Control
                                type="text"
                                name="phone"
                                value={formData.phone}
                                onChange={handleInputChange}
                            />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <p>Contact Email Address : </p>
                            <Form.Control
                                type="email"
                                name="email"
                                value={formData.email}
                                onChange={handleInputChange}
                            />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <p>Company Location : </p>
                            <Form.Control
                                type="text"
                                name="location"
                                value={formData.location}
                                onChange={handleInputChange}
                            />
                        </Form.Group>
                        {/* <Form.Group className="mb-3">
                            <p>Properties Type : </p>
                            <Form.Control
                                type="text"
                                name="property_type"
                                value={formData.property_type}
                                onChange={handleInputChange}
                            />
                        </Form.Group> */}
                        <div className="d-flex justify-content-end">
                            <Button variant="secondary" onClick={handleCloseModal} className="me-2">
                                Cancel
                            </Button>
                            <Button variant="primary" type="submit" style={{ backgroundColor: "#3498db", border: "none" }}>
                                Save Changes
                            </Button>
                        </div>
                    </Form>
                </Modal.Body>
            </Modal>
        </div>
    ) : null;
}